import React, { useState } from "react";
import { TimePicker, Checkbox, Switch, message, Button } from "antd";
import dayjs from "dayjs";
import { postRequest, putRequest } from "../service/index";
import AttendanceRules, { migrateSettings, normalizeRelaxableForForm, sanitizeRelaxableSettings } from "./AttendanceRules";

const defaultSettings = {
  regDays: 7,
  applyDays: 3,
  applyPeriod: 'Month',
  isAttendanceRulesEnabled: true,
  isEarlyOutRulesEnabled: true,
  isRelaxableSettingsEnabled: false,
  lateRuleType: 'count',
  rule1: {
    lateCount: 3,
    lateMinutes: 15,
    latePeriod: 'Month',
    lateDeduction: 'half'
  },
  rule2: {
    lateMinutes: 15,
    lateTimeUnit: 'minute',
    lateTimeDeduction: '1'
  },
  earlyOutRuleType: 'count',
  earlyOutRule1: {
    earlyOutCount: 3,
    earlyOutMinutes: 15,
    earlyOutPeriod: 'Month',
    earlyOutDeduction: 'half'
  },
  earlyOutRule2: {
    earlyOutMinutes: 15,
    earlyOutTimeUnit: 'minute',
    earlyOutTimeDeduction: '1'
  },
  relaxable: {
    requiredHours: 9,
    period: 'Day',
    flexibleStartHours: 2,
    lateArrivalDeduction: 'quarter',
    relaxableRuleType: 'lateArrival'
  }
};

const EditShiftModal = (props) => {
  const { open, onClose, shift } = props;
  const [activeTab, setActiveTab] = useState("shift");
  const [shiftName, setShiftName] = useState(shift?.title || "");
  const [startTime, setStartTime] = useState(shift?.startTime ? dayjs(shift.startTime, "HH:mm") : null);
  const [endTime, setEndTime] = useState(shift?.endTime ? dayjs(shift.endTime, "HH:mm") : null);
  const [workingHours, setWorkingHours] = useState(shift?.totalHours ? String(shift.totalHours) : "");
  const [breakTimeEnabled, setBreakTimeEnabled] = useState(!!shift?.breakTimeStart);
  const [breakStartTime, setBreakStartTime] = useState(shift?.breakTimeStart ? dayjs(shift.breakTimeStart, "HH:mm") : null);
  const [breakEndTime, setBreakEndTime] = useState(shift?.breakTimeEnd ? dayjs(shift.breakTimeEnd, "HH:mm") : null);
  const [selectedDays, setSelectedDays] = useState(shift?.days ? shift.days.split(", ") : []);
  const [loading, setLoading] = useState(false);
  const [settingsData, setSettingsData] = useState(defaultSettings); // TODO: If shift.settings exists, use it
  const [dayChange, setDayChange] = useState(false);

  // Update state when shift changes
  React.useEffect(() => {
    if (open && shift) {
      setShiftName(shift.title || "");
      setStartTime(shift.startTime ? dayjs(shift.startTime, "HH:mm") : null);
      setEndTime(shift.endTime ? dayjs(shift.endTime, "HH:mm") : null);
      setWorkingHours(shift.totalHours ? String(shift.totalHours) : "");
      setBreakTimeEnabled(!!shift.breakTimeStart);
      setBreakStartTime(shift.breakTimeStart ? dayjs(shift.breakTimeStart, "HH:mm") : null);
      setBreakEndTime(shift.breakTimeEnd ? dayjs(shift.breakTimeEnd, "HH:mm") : null);
      setSelectedDays(
        shift.workingDays
          ? Array.isArray(shift.workingDays)
            ? shift.workingDays
            : shift.workingDays.split(", ")
          : []
      );
      // Migrate old settings structure if needed
      const migratedSettings = shift.settings ? migrateSettings({
        ...defaultSettings,
        ...shift.settings,
        earlyOutRuleType: shift.settings.earlyOutRuleType || 'count',
        earlyOutRule1: shift.settings.earlyOutRule1 || {
          earlyOutCount: shift.settings.earlyOutCount || 3,
          earlyOutMinutes: shift.settings.earlyOutMinutes || 15,
          earlyOutPeriod: shift.settings.earlyOutPeriod || 'Month',
          earlyOutDeduction: shift.settings.earlyOutDeduction || 'half'
        },
        earlyOutRule2: shift.settings.earlyOutRule2 || {
          earlyOutMinutes: 15,
          earlyOutTimeUnit: 'minute',
          earlyOutTimeDeduction: '1'
        },
        relaxable: normalizeRelaxableForForm(shift.settings.relaxable)
      }) : defaultSettings;
      setSettingsData(migratedSettings);
      setDayChange(!!shift.dayChange);
    }
  }, [shift, open]);

  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const calculateWorkingHours = (start, end) => {
    if (!start || !end) return;
    const startMoment = dayjs(start);
    let endMoment = dayjs(end);
    
    // Check if shift spans across days
    const spansAcrossDays = endMoment.isBefore(startMoment);
    setDayChange(spansAcrossDays);
    
    if (spansAcrossDays) {
      endMoment = endMoment.add(1, 'day');
    }
    let diffHours = endMoment.diff(startMoment, 'hour', true);
    if (breakTimeEnabled && breakStartTime && breakEndTime) {
      const breakStart = dayjs(breakStartTime);
      let breakEnd = dayjs(breakEndTime);
      if (breakEnd.isBefore(breakStart)) {
        breakEnd = breakEnd.add(1, 'day');
      }
      const breakHours = breakEnd.diff(breakStart, 'hour', true);
      diffHours -= breakHours;
    }
    diffHours = Math.max(0, diffHours);
    setWorkingHours(diffHours.toFixed(1));
  };

  const handleStartTimeChange = (time) => {
    setStartTime(time);
    calculateWorkingHours(time, endTime);
  };

  const handleEndTimeChange = (time) => {
    setEndTime(time);
    calculateWorkingHours(startTime, time);
  };

  const handleBreakStartTimeChange = (time) => {
    setBreakStartTime(time);
    calculateWorkingHours(startTime, endTime);
  };

  const handleBreakEndTimeChange = (time) => {
    setBreakEndTime(time);
    calculateWorkingHours(startTime, endTime);
  };

  const handleBreakTimeToggle = (checked) => {
    setBreakTimeEnabled(checked);
    calculateWorkingHours(startTime, endTime);
  };

  function filterSettings(settings) {
    return {
      regDays: settings.regDays,
      applyDays: settings.applyDays,
      applyPeriod: settings.applyPeriod,
      isAttendanceRulesEnabled: settings.isAttendanceRulesEnabled,
      lateRuleType: settings.lateRuleType,
      rule1: settings.rule1,
      rule2: settings.rule2,
      isEarlyOutRulesEnabled: settings.isEarlyOutRulesEnabled,
      earlyOutRuleType: settings.earlyOutRuleType,
      earlyOutRule1: settings.earlyOutRule1,
      earlyOutRule2: settings.earlyOutRule2,
      isRelaxableSettingsEnabled: settings.isRelaxableSettingsEnabled,
      relaxable: sanitizeRelaxableSettings(settings.relaxable),
    };
  }

  const handleSubmit = async () => {
    if (!shift) return;
    try {
      setLoading(true);
      // Deep copy settingsData to avoid mutating state
      const settingsCopy = JSON.parse(JSON.stringify(settingsData));
      
      // Convert rule2.lateMinutes to minutes if lateTimeUnit is 'hour'
      if (
        settingsCopy.rule2 &&
        settingsCopy.rule2.lateTimeUnit === 'hour' &&
        settingsCopy.rule2.lateMinutes
      ) {
        const hours = parseFloat(settingsCopy.rule2.lateMinutes);
        if (!isNaN(hours)) {
          settingsCopy.rule2.lateMinutes = hours * 60;
          settingsCopy.rule2.lateTimeUnit = 'minute'; // Normalize unit for backend
        }
      }
      
      // Convert earlyOutRule2.earlyOutMinutes to minutes if earlyOutTimeUnit is 'hour'
      if (
        settingsCopy.earlyOutRule2 &&
        settingsCopy.earlyOutRule2.earlyOutTimeUnit === 'hour' &&
        settingsCopy.earlyOutRule2.earlyOutMinutes
      ) {
        const hours = parseFloat(settingsCopy.earlyOutRule2.earlyOutMinutes);
        if (!isNaN(hours)) {
          settingsCopy.earlyOutRule2.earlyOutMinutes = hours * 60;
          settingsCopy.earlyOutRule2.earlyOutTimeUnit = 'minute'; // Normalize unit for backend
        }
      }
      
      // Clean settingsData to avoid empty strings for required numeric fields
      const cleanedSettings = {
        ...settingsCopy,
        rule1: {
          ...settingsCopy.rule1,
          lateCount: settingsCopy.rule1.lateCount ? Number(settingsCopy.rule1.lateCount) : 0,
          lateMinutes: settingsCopy.rule1.lateMinutes ? Number(settingsCopy.rule1.lateMinutes) : 0,
          latePeriod: settingsCopy.rule1.latePeriod || 'Month',
          lateDeduction: settingsCopy.rule1.lateDeduction || 'half',
        },
        rule2: {
          ...settingsCopy.rule2,
          lateMinutes: settingsCopy.rule2.lateMinutes ? Number(settingsCopy.rule2.lateMinutes) : 0,
          lateTimeUnit: settingsCopy.rule2.lateTimeUnit || 'minute',
          lateTimeDeduction: settingsCopy.rule2.lateTimeDeduction || '1',
        },
        earlyOutRule1: {
          ...settingsCopy.earlyOutRule1,
          earlyOutCount: settingsCopy.earlyOutRule1.earlyOutCount ? Number(settingsCopy.earlyOutRule1.earlyOutCount) : 0,
          earlyOutMinutes: settingsCopy.earlyOutRule1.earlyOutMinutes ? Number(settingsCopy.earlyOutRule1.earlyOutMinutes) : 0,
          earlyOutPeriod: settingsCopy.earlyOutRule1.earlyOutPeriod || 'Month',
          earlyOutDeduction: settingsCopy.earlyOutRule1.earlyOutDeduction || 'half',
        },
        earlyOutRule2: {
          ...settingsCopy.earlyOutRule2,
          earlyOutMinutes: settingsCopy.earlyOutRule2.earlyOutMinutes ? Number(settingsCopy.earlyOutRule2.earlyOutMinutes) : 0,
          earlyOutTimeUnit: settingsCopy.earlyOutRule2.earlyOutTimeUnit || 'minute',
          earlyOutTimeDeduction: settingsCopy.earlyOutRule2.earlyOutTimeDeduction || '1',
        },
      };
      const data = {
        shiftName,
        workingHours: parseFloat(workingHours),
        startTime: startTime?.format("HH:mm"),
        endTime: endTime?.format("HH:mm"),
        breakTimeStart: breakTimeEnabled ? breakStartTime?.format("HH:mm") : null,
        breakTimeEnd: breakTimeEnabled ? breakEndTime?.format("HH:mm") : null,
        workingDays: selectedDays,
        dayChange: dayChange,
        settings: filterSettings(cleanedSettings)
      };
      
      const response = await putRequest(`/api/companies/shifts?shiftId=${shift.id}`, data);
      if (response?.message === "Shift updated successfully" || response?.success) {
        message.success("Shift updated successfully!");
        onClose();
      } else {
        message.error(response?.error || "Failed to update shift. Please try again.");
      }
    } catch (error) {
      console.error("Error updating shift:", error);
      message.error(error?.message || "Failed to update shift. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDayChange = (day, checked) => {
    if (checked) {
      setSelectedDays([...selectedDays, day]);
    } else {
      setSelectedDays(selectedDays.filter(d => d !== day));
    }
  };

  if (!open || !shift) return null;
  return (
    <div className="h-[70vh] overflow-y-auto pr-3">
      <div className="border-b border-gray-200 flex space-x-8 mb-4">
        <button
          className={`py-2 px-1 text-md font-medium ${
            activeTab === "shift"
              ? "border-b-2 border-indigo-700 text-indigo-700"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("shift")}
        >
          Shift
        </button>
        <button
          className={`py-2 px-1 text-md font-medium ${
            activeTab === "settings"
              ? "border-b-2 border-indigo-700 text-indigo-700"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("settings")}
        >
          Settings
        </button>
      </div>
      {activeTab === "shift" && (
        <>
          <div className="flex flex-col gap-3 ">
      <div className="flex flex-col gap-1">
        <label htmlFor="shift-name" className="text-md">
          Shift Name
        </label>
        <input
          type="text"
          id="shift-name"
          className="border border-gray-400 outline-hidden px-2 py-1 rounded-md text-md"
          placeholder="i.e Morning Shift"
          value={shiftName}
          onChange={(e) => setShiftName(e.target.value)}
        />
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="flex flex-col col-span-6 gap-2">
          <label htmlFor="shift-start" className="text-md">
            Start Time(e.g 09:00)
          </label>
          <TimePicker 
            id="shift-start" 
            value={startTime}
            onChange={handleStartTimeChange} 
            format="HH:mm" 
          />
        </div>
        <div className="flex flex-col gap-2 col-span-6">
          <label htmlFor="shift-end" className="text-md">
            End Time (e.g 17:00)
          </label>
          <TimePicker 
            id="shift-end" 
            value={endTime}
            onChange={handleEndTimeChange} 
            format="HH:mm" 
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <label className="text-md">Break Time</label>
          <Switch 
            checked={breakTimeEnabled}
            onChange={handleBreakTimeToggle}
          />
        </div>
        {breakTimeEnabled && (
          <div className="grid grid-cols-12 gap-4">
            <div className="flex flex-col col-span-6 gap-2">
              <label htmlFor="break-start" className="text-md">
                Break Start Time
              </label>
              <TimePicker 
                id="break-start" 
                value={breakStartTime}
                onChange={handleBreakStartTimeChange} 
                format="HH:mm" 
              />
            </div>
            <div className="flex flex-col gap-2 col-span-6">
              <label htmlFor="break-end" className="text-md">
                Break End Time
              </label>
              <TimePicker 
                id="break-end" 
                value={breakEndTime}
                onChange={handleBreakEndTimeChange} 
                format="HH:mm" 
              />
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="working-hours" className="text-md">
          Working Hours
        </label>
        <input
          type="text"
          id="working-hours"
          className="border border-gray-400 outline-hidden px-2 py-1 rounded-md text-md"
          value={workingHours}
          readOnly
          placeholder="Auto-calculated working hours"
        />
      </div>
      
      {startTime && endTime && (
        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
          <span className="text-sm font-medium">Shift Type:</span>
          <span className={`text-sm px-2 py-1 rounded-full ${
            dayChange 
              ? 'bg-orange-100 text-orange-700' 
              : 'bg-green-100 text-green-700'
          }`}>
            {dayChange ? 'Overnight Shift (spans across days)' : 'Same Day Shift'}
          </span>
        </div>
      )}
           
      <h4 className="font-semibold">Working Days</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-2 gap-y-2">
        {days.map((day) => (
                <div className="flex items-center gap-3 text-md" key={day}>
            <Checkbox 
              id={day}
              onChange={(e) => handleDayChange(day, e.target.checked)}
              checked={selectedDays.includes(day)}
            >
              {day}
            </Checkbox>
          </div>
        ))}
      </div>
    </div>
    </>
      )}
      {activeTab === "settings" && (
        <div>
          <AttendanceRules settingsData={settingsData} setSettingsData={setSettingsData} />
        </div>
      )}
      {/* Move the button group here so it's always visible */}

      <div className="flex justify-end gap-2 mt-4">
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button 
            type="primary" 
            onClick={handleSubmit}
            loading={loading}
            className="bg-primary hover:bg-primary/80"
          >
             {loading ? "Updating..." : "Update Shift"}
          </Button>
        </div>
    </div>
  );
};

export default EditShiftModal;
